import { useEffect, useRef, useState } from 'react';

export interface Step {
  icon: string;
  title: string;
  description: string;
  detail?: string;
}

interface HowItWorksStepsProps {
  steps: Step[];
  variant?: 'horizontal' | 'grid';
  title?: string;
  subtitle?: string;
  eyebrow?: string;
  ctaText?: string;
  ctaHref?: string;
  ctaNote?: string;
  theme?: 'light' | 'dark' | 'warm';
  showAnimation?: boolean;
}

export default function HowItWorksSteps({
  steps,
  variant = 'grid',
  title = 'How It Works',
  subtitle,
  eyebrow,
  ctaText,
  ctaHref,
  ctaNote,
  theme = 'light',
  showAnimation = true,
}: HowItWorksStepsProps) {
  const [visible, setVisible] = useState(!showAnimation);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showAnimation) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.15 }
    );
    
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [showAnimation]);

  const bgColor = theme === 'dark' ? 'bg-[#1A2332]' : theme === 'warm' ? 'bg-[#F7F5F0]' : 'bg-white';
  const textColor = theme === 'dark' ? 'text-white' : 'text-[#1A2332]';
  const subtextColor = theme === 'dark' ? 'text-white/70' : 'text-[#4A5568]';

  return (
    <section ref={ref} className={`py-16 sm:py-20 ${bgColor} border-t border-[#E8E2D6]`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-12 sm:mb-14">
            {eyebrow && (
              <div className="inline-flex items-center gap-2 bg-[#E8B84B]/20 border border-[#E8B84B]/40 rounded-full px-4 py-2 mb-4">
                <i className="ri-route-line text-[#E8B84B]" aria-hidden="true"></i>
                <span className="text-sm font-semibold text-[#1C2B1E]">{eyebrow}</span>
              </div>
            )}
            
            <h2 className={`font-['Oswald'] text-3xl sm:text-4xl md:text-5xl font-bold ${textColor} mb-4`}>
              {title}
            </h2>
            
            {title !== 'How It Works' && (
              <div className="w-24 h-1 bg-[#E8B84B] mx-auto mb-6"></div>
            )}
            
            {subtitle && (
              <p className={`text-base sm:text-lg ${subtextColor} max-w-2xl mx-auto`}>
                {subtitle}
              </p>
            )}
          </div>

          {/* Steps */}
          <div className="relative">
            {/* Connector line for horizontal layout */}
            {variant === 'horizontal' && (
              <div 
                className="hidden lg:block absolute top-[52px] left-[12.5%] right-[12.5%] h-0.5 bg-[#E8E2D6] z-0" 
                aria-hidden="true" 
              />
            )}

            <ol 
              className={`grid gap-6 sm:gap-8 relative z-10 list-none p-0 ${
                variant === 'horizontal' 
                  ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' 
                  : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
              }`}
              aria-label={`${title} — ${steps.length} steps`}
            >
              {steps.map((step, index) => (
                <li
                  key={index}
                  className={variant === 'horizontal' ? 'flex flex-col items-center text-center' : 'relative'}
                  style={
                    showAnimation
                      ? {
                          opacity: visible ? 1 : 0,
                          transform: visible ? 'translateY(0)' : 'translateY(28px)',
                          transition: `opacity 0.5s ease ${index * 0.12}s, transform 0.5s ease ${index * 0.12}s`,
                        }
                      : undefined
                  }
                >
                  {/* Connector line for grid layout */}
                  {variant === 'grid' && index < steps.length - 1 && (
                    <div 
                      className="hidden lg:block absolute top-16 left-[60%] w-[80%] h-0.5 bg-[#E8E2D6]" 
                      aria-hidden="true"
                    ></div>
                  )}

                  {variant === 'horizontal' ? (
                    // Horizontal variant (Broker style)
                    <>
                      <div
                        className="w-16 h-16 sm:w-[72px] sm:h-[72px] flex items-center justify-center rounded-full bg-[#E8B84B] shadow-md mb-4 sm:mb-5 relative"
                        aria-hidden="true"
                      >
                        <i className={`${step.icon} text-xl sm:text-2xl text-[#1C2B1E]`}></i>
                        <span className="absolute -top-1.5 -right-1.5 w-6 h-6 flex items-center justify-center bg-white border-2 border-[#E8E2D6] rounded-full text-[10px] font-bold text-[#1C2B1E]">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                      </div>

                      <h3 className={`font-['Oswald'] text-base sm:text-lg font-bold ${textColor} mb-2`}>
                        {step.title}
                      </h3>
                      <p className={`${subtextColor} text-xs sm:text-sm leading-relaxed mb-3`}>
                        {step.description}
                      </p>
                      {step.detail && (
                        <span className="inline-block text-xs font-semibold text-[#92650a] bg-[#E8B84B]/10 border border-[#E8B84B]/30 rounded-full px-3 py-1">
                          {step.detail}
                        </span>
                      )}
                    </>
                  ) : (
                    // Grid variant (Trade-in / Sell Your Car style)
                    <div className={`relative ${theme === 'warm' ? 'bg-[#F7F5F0]' : 'bg-white'} rounded-2xl p-6 sm:p-8 border-2 border-[#E8E2D6] hover:border-[#E8B84B] transition-all text-center`}>
                      <div 
                        className="absolute -top-4 left-1/2 -translate-x-1/2 w-12 h-12 bg-[#1A2332] text-white rounded-full flex items-center justify-center font-['Oswald'] text-2xl font-bold" 
                        aria-hidden="true"
                      >
                        {index + 1}
                      </div>

                      <div 
                        className="w-20 h-20 bg-[#E8B84B] rounded-full flex items-center justify-center mx-auto mb-6 mt-4" 
                        aria-hidden="true"
                      >
                        <i className={`${step.icon} text-3xl sm:text-4xl text-[#1A2332]`}></i>
                      </div>

                      <h3 className={`font-['Oswald'] text-xl sm:text-2xl font-bold ${textColor} mb-3`}>
                        {step.title}
                      </h3>
                      <p className={`${subtextColor} text-sm sm:text-base leading-relaxed`}>
                        {step.description}
                      </p>
                    </div>
                  )}
                </li>
              ))}
            </ol>
          </div>

          {/* CTA */}
          {ctaText && ctaHref && (
            <div className="mt-12 text-center">
              {ctaNote && (
                <p className={`text-sm ${subtextColor} mb-4 italic`}>{ctaNote}</p>
              )}
              <a
                href={ctaHref}
                className="inline-flex items-center gap-2 bg-[#1A2332] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold hover:bg-[#2C3E55] transition-all whitespace-nowrap cursor-pointer text-sm sm:text-base"
              >
                {ctaText}
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </a>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}